import { EventEmitter, Injectable, Output } from '@angular/core';
import { SpaContext } from '../Classes/spacontext';
import { Messaging } from '../Injectables/Models/Messaging/messaging.models';
import { MessagingInjectable } from '../Injectables/messaging.injectable';
import { CasesServiceV2 } from './api/v2/cases.service';
import { CaseApiModel } from './api/v2/Models/CaseApiModel';
import { DropInApiModel } from './api/v2/Models/DropInApiModel';
import { TimeslotApiModel } from './api/v2/Models/TimeslotApiModel';
import { DropInUnitServiceV2 } from './api/v2/Units/dropIn.service';
import { LocalityType, TimeslotServiceV2 } from './api/v2/Units/timeslot.service';
import { AuthContextService } from './authContext.service';
import { SpaEventService } from './spaEvents.service';

type SchedulerFor = 'ForAll' | 'ForPatient' | 'ForClient' | 'ForOutsider';

@Injectable()
export class ScheduledDataService {
	@Output() onGetCases = new EventEmitter<Array<CaseApiModel>>();
	@Output() onGetTodaysTimeslots = new EventEmitter<Array<TimeslotApiModel>>();
	@Output() onGetDropInRoomsStatus = new EventEmitter<DropInApiModel[]>();

	intervals: Array<{ intervalId: number; name: string; for: SchedulerFor }> = [];

	dataCache: {
		cases: Array<CaseApiModel>;
	} = {
		cases: [],
	};

	constructor(
		public _spaContext: SpaContext,
		private _spaEventService: SpaEventService,
		private _casesService: CasesServiceV2,
		private _timeslotService: TimeslotServiceV2,
		private _dropInUnitService: DropInUnitServiceV2,
		private _messagingInjectable: MessagingInjectable,
		private authContextService: AuthContextService,
	) {
		// Initial start

		// The event emitters that is triggered from this is only used in menu.component.ts
		// which is not used right now
		// TODO: Do we need these schedulers when implementing a toggle?
		// if (!this._spaContext.unitConfig.isPreNodeSelection()) {
		// 	this.startSchedulers_All();
		// }
		if (this.authContextService.isClient()) {
			this.startSchedulers_Client();
		}
		if (this.authContextService.isOutsider()) {
			this.startSchedulers_Outsider();
		}

		// On event start
		this._spaEventService.onSignedIn.subscribe(() => {
			this.startSchedulers_Client();
		});
		this._spaEventService.onSignedOut.subscribe(() => {
			this.stopSchedulers('ForClient');
			this.stopSchedulers('ForPatient');
		});

		this._spaEventService.onSignedInAsOutsider.subscribe(() => {
			this.startSchedulers_Outsider();
		});

		this._spaEventService.onSignedOutAsOutsider.subscribe(() => {
			this.stopSchedulers('ForOutsider');
			this.stopSchedulers('ForPatient');
		});

		this._spaEventService.onCallEnded.subscribe(() => {
			this.getCases();
		});

		this.attachListerners();
	}

	pushCachedCases(): void {
		this.onGetCases.emit(this.dataCache.cases);
	}

	private attachListerners(): void {
		this._messagingInjectable.onMessages.subscribe((caseMessages: Messaging.CaseMessagesSocketModel) => {
			if (caseMessages.data.every((m) => m.sender?.isMe)) return;

			const cachedCase = this.dataCache.cases.find((c) => c.MessageConversationId === caseMessages.caseId);
			if (cachedCase?.UnhandledIssues.indexOf('UnreadMessages') === -1) cachedCase.UnhandledIssues.push('UnreadMessages');
			this.onGetCases.emit(this.dataCache.cases);
		});

		this._messagingInjectable.onHasReadUntil.subscribe((readUntil: Messaging.HasReadUntilSocketModel) => {
			const cachedCase = this.dataCache.cases.find((c) => c.MessageConversationId === readUntil.caseId);
			cachedCase.UnhandledIssues = cachedCase.UnhandledIssues.filter((i) => i !== 'UnreadMessages');
			this.onGetCases.emit(this.dataCache.cases);
		});
	}

	public stopSchedulers(access: SchedulerFor): void {
		this.intervals
			.filter((s) => s.for === access)
			.forEach((s) => {
				clearTimeout(s.intervalId);
			});

		this.intervals = this.intervals.filter((s) => s.for !== access);
	}

	private runAndScheduleFunction(func: () => void, name: string, forUser: SchedulerFor, interval = 60000): void {
		if (typeof this.intervals.find((i) => i.name === name) !== 'undefined') return;

		const intervalId = window.setInterval(() => {
			func();
		}, interval);
		this.intervals.push({ intervalId: intervalId, name: name, for: forUser });

		func();
	}

	/**
	 * Scheduler initiators
	 */
	private startSchedulers_Client(): void {
		this.runAndScheduleFunction(this.getCases, 'getCases', 'ForClient');
	}
	private startSchedulers_Outsider(): void {
		// noop
	}
	// biome-ignore lint/correctness/noUnusedPrivateClassMembers: suppressed due to migration - should be fixed
	private startSchedulers_Patient(): void {
		//this.runAndScheduleFunction(this.getActiveDropInTickets, 'activeDropInTickets', 'ForClient');
	}

	public startSchedulers_All(): void {
		this.runAndScheduleFunction(this.getTodaysTimeslots, 'getTodaysTimeslots', 'ForAll');
		this.runAndScheduleFunction(this.getDropInRoomsStatus, 'getDropInRoomsStatus', 'ForAll');
	}

	/**
	 * Scheduled methods
	 */
	public getCases = (): void => {
		const from = new Date();
		from.setFullYear(from.getFullYear() - 1);
		from.setHours(0, 0, 0, 0);

		this._casesService.get(from, null, false).subscribe((response) => {
			this.dataCache.cases = response.data;

			this.onGetCases.emit(response.data);
		});
	};

	private getTodaysTimeslots = (): void => {
		this._timeslotService
			.get(
				{
					specialityId: undefined,
					conditionId: undefined,
					resourceId: undefined,
					filterableUnitId: undefined,
					date: undefined,
					fromDate: new Date().format('yyyy-MM-dd'),
					toDate: new Date().format('yyyy-MM-dd'),
					fromTime: new Date(new Date().setHours(0, 0)).format('HH:mm:ss'),
					toTime: new Date(new Date().setHours(23, 59)).format('HH:mm:ss'),
					localityType: LocalityType.All,
				},
				false,
			)
			.subscribe((response) => {
				this.onGetTodaysTimeslots.emit(response.data);
			});
	};

	private getDropInRoomsStatus = (): void => {
		this._dropInUnitService.get(false).subscribe((response) => {
			this.onGetDropInRoomsStatus.emit(response.data);
		});
	};
}
